import { findServiceUrls, findServiceUrlsGlobal, cacheServiceUrls, getCachedServiceUrls } from './discoveryService';
import { encryptObject, decryptObject } from './cryptoService';

const TOKEN_KEY = 'pacto_api_token';
const CHAVE_KEY = 'pacto_chave';
const USER_DATA_KEY = 'pacto_user_data';
const TOKEN_EXPIRY_KEY = 'pacto_token_expiry';
const LOGIN_URL_KEY = 'pacto_login_url';

const LOGIN_ENDPOINT_PATH = '/prest/lg-auth/v1/login';
const VALIDATE_ENDPOINT_PATH = '/prest/vinculo/execucao-status';

const FALLBACK_LOGIN_BASE_URL = 'https://app.pactosolucoes.com.br/login';

// Função para decodificar JWT (sem validar assinatura)
const decodeJWT = (token) => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.error('❌ Token JWT inválido (não tem 3 partes)');
      return null;
    }

    // Decodifica o payload (segunda parte)
    const payload = parts[1];
    const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));

    return decoded;
  } catch (error) {
    console.error('❌ Erro ao decodificar JWT:', error);
    return null;
  }
};

export const login = async (chave, username, senha, recaptchaToken = null) => {
  try {
    let loginUrl;
    let loginBaseUrl;
    let discoveryResult;

    if (chave && chave.trim()) {
      discoveryResult = await findServiceUrls(chave);
    } else {
      discoveryResult = await findServiceUrlsGlobal();
    }

    if (discoveryResult.success && discoveryResult.serviceUrls.loginAppUrl) {
      loginBaseUrl = discoveryResult.serviceUrls.loginAppUrl;
      loginUrl = loginBaseUrl + LOGIN_ENDPOINT_PATH;
    } else {
      loginBaseUrl = FALLBACK_LOGIN_BASE_URL;
      loginUrl = loginBaseUrl + LOGIN_ENDPOINT_PATH;
    }

    const credentials = {
      username: username.trim(),
      senha: senha
    };

    if (chave && chave.trim()) {
      credentials.chave = chave.trim();
    }

    let encryptedData;
    try {
      encryptedData = encryptObject(credentials);
    } catch (error) {
      return {
        success: false,
        error: 'Erro ao processar credenciais. Tente novamente.'
      };
    }

    const headers = {
      'Content-Type': 'application/json'
    };

    if (recaptchaToken) {
      headers['g-token'] = recaptchaToken;
    }

    const requestBody = {
      data: encryptedData
    };

    const response = await fetch(loginUrl, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.message ||
               errorData?.content?.message ||
               'Credenciais inválidas. Verifique seus dados e tente novamente.'
      };
    }

    const result = await response.json();

    let authData;

    if (result.content && typeof result.content === 'string') {
      try {
        const decryptedContent = decryptObject(result.content);
        authData = decryptedContent;
      } catch (error) {
        return {
          success: false,
          error: 'Erro ao processar resposta do servidor'
        };
      }
    } else {
      authData = result;
    }

    const token = authData?.token || authData?.accessToken || authData?.jwt;

    if (!token) {
      return {
        success: false,
        error: 'Token de autenticação não encontrado na resposta'
      };
    }

    const decodedToken = decodeJWT(token);

    const chaveToSave = chave ||
                        authData?.chave ||
                        authData?.dados?.chave ||
                        authData?.dadosAcesso?.chave;

    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(LOGIN_URL_KEY, loginBaseUrl);

    if (decodedToken && decodedToken.exp) {
      localStorage.setItem(TOKEN_EXPIRY_KEY, decodedToken.exp.toString());
    }

    if (chaveToSave && chaveToSave.trim()) {
      localStorage.setItem(CHAVE_KEY, chaveToSave.trim());
    }

    if (authData.dados) {
      localStorage.setItem(USER_DATA_KEY, JSON.stringify(authData.dados));
    }

    if (discoveryResult && discoveryResult.success && discoveryResult.serviceUrls) {
      cacheServiceUrls(discoveryResult.serviceUrls);
    }

    return {
      success: true,
      token: token,
      userData: authData.dados,
      dadosAcesso: authData.dadosAcesso
    };

  } catch (error) {
    return {
      success: false,
      error: 'Erro de conexão. Verifique sua internet e tente novamente.'
    };
  }
};

export const validateToken = async () => {
  const token = localStorage.getItem(TOKEN_KEY);

  if (!token) {
    return false;
  }

  const decodedToken = decodeJWT(token);

  if (!decodedToken) {
    return false;
  }

  if (decodedToken.exp) {
    const now = Math.floor(Date.now() / 1000);
    const isExpired = now >= decodedToken.exp;

    if (isExpired) {
      return false;
    }
  }

  try {
    let loginBaseUrl = localStorage.getItem(LOGIN_URL_KEY);

    if (!loginBaseUrl) {
      const cachedUrls = getCachedServiceUrls();

      if (cachedUrls && cachedUrls.loginAppUrl) {
        loginBaseUrl = cachedUrls.loginAppUrl;
      } else {
        const discoveryResult = await findServiceUrlsGlobal();

        if (discoveryResult.success && discoveryResult.serviceUrls.loginAppUrl) {
          loginBaseUrl = discoveryResult.serviceUrls.loginAppUrl;
          localStorage.setItem(LOGIN_URL_KEY, loginBaseUrl);
        } else {
          return false;
        }
      }
    }

    const validateUrl = `${loginBaseUrl}${VALIDATE_ENDPOINT_PATH}`;

    const response = await fetch(validateUrl, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    return response.ok;

  } catch (error) {
    return false;
  }
};

export const getToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

export const logout = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(CHAVE_KEY);
  localStorage.removeItem(USER_DATA_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
  localStorage.removeItem(LOGIN_URL_KEY);
  localStorage.removeItem('pacto_service_urls');
};

export const isAuthenticated = () => {
  return !!localStorage.getItem(TOKEN_KEY);
};

export const getUserData = () => {
  const userData = localStorage.getItem(USER_DATA_KEY);
  return userData ? JSON.parse(userData) : null;
};

export const getTokenExpiry = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return null;

  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return null;

  return {
    expiresAt: new Date(decoded.exp * 1000),
    expiresAtTimestamp: decoded.exp,
    isExpired: Math.floor(Date.now() / 1000) >= decoded.exp,
    expiresInMinutes: Math.floor((decoded.exp - Math.floor(Date.now() / 1000)) / 60)
  };
};

